import { CommonModule, CurrencyPipe, isPlatformBrowser } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import {
  APP_INITIALIZER,
  InjectionToken,
  NgModule,
  PLATFORM_ID,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import resolveConfig from 'tailwindcss/resolveConfig';
import { MatchMediaService } from './services/match-media/match-media.service';
import tailwindConfig from '~/../../tailwind.config';
import { APP_STORAGE } from '~/app.token';
import { CMS_BASE_URL } from '~/cms/services/cms/cms.service';

import { environment } from '~environments/environment';
import { ConsoleLoggerService } from './console-logger.service';
import { AppDataTestDirective } from './directives/app-data-test.directive';
import { AppModeEmbedDirective } from './directives/app-mode-embed.directive';
import { AppModeFullDirective } from './directives/app-mode-full.directive';
import { ListItemDirective } from './directives/list-item.directive';
import { ObserveElementDirective } from './directives/observe-element.directive';
import { LoadingDirective } from './directives/loading.directive';
import { APIThrottleInterceptor } from './interceptors/api-throttle.interceptor';
import { ExtraHeadersInterceptor } from './interceptors/extra-headers.interceptor';
import { Logger } from './logger.service';
import { DisplayAgeRangePipe } from './pipes/display-age-range.pipe';
import { DisplayAgePipe } from './pipes/display-age.pipe';
import { DisplayLevelPipe } from './pipes/display-level.pipe';
import { DisplayPricePipe } from './pipes/display-price.pipe';
import { PriceConversionPipe } from './pipes/price-conversion.pipe';
import { DisplayStartingPricePipe } from './pipes/display-starting-price.pipe';
import { FileSizePipe } from './pipes/file-size.pipe';
import { LocalizedDatePipe } from './pipes/localized-date.pipe';
import { Nl2BrPipe } from './pipes/nl2br.pipe';
import { PrivacyManagerService } from './services/privacy-manager/privacy-manager';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { SafeUrlPipe } from './pipes/safe-url.pipe';
import { StripHtmlPipe } from './pipes/stripHtml.pipe';
import { RedirectionService } from './services/redirection/redirection.service';
import { SportsService } from './services/sports/sports.service';
import { DummyStorageService } from './services/storage/dummy-storage.service';
import {
  GTM_ID,
  TagManagerService,
} from './services/tag-manager/tag-manager.service';
import setupDatadog from 'setupDatadog';

export const ACTIVITIES_BASE_URL = new InjectionToken<string>(
  'Activities BASE_URL'
);
export const ACTIVITIES_API_KEY = new InjectionToken<string>(
  'Activities API_KEY'
);

export const PFS_BASE_URL = new InjectionToken<string>('PFS BASE_URL');

export const ADVICE_BASE_URL = new InjectionToken<string>('ADVICE BASE_URL');
export const ADVICE_API_KEY = new InjectionToken<string>('ADVICE API_KEY');

export const COACH_STORE_IOS_URL = new InjectionToken<string>('Coach IOS_URL');
export const COACH_STORE_ANDROID_URL = new InjectionToken<string>(
  'Coach ANDROID_URL'
);

export const CDN_ROOT = new InjectionToken<string>('Host root folder');
export const CDN_HOST = new InjectionToken<string>('Host of the CDN');
export const TAILWIND_CONFIG = new InjectionToken<object>(
  'Tailwind configuration'
);

const ACTIVITIES_INTERCEPTOR = new InjectionToken<ExtraHeadersInterceptor>(
  'Manually constructed interceptor',
  {
    providedIn: 'root',
    factory: () =>
      new ExtraHeadersInterceptor(environment.applications.activities.url, {
        'x-api-key': environment.applications.activities.api_key,
      }),
  }
);

export function sportsServiceFactory(sportsService: SportsService): () => void {
  return () =>
    sportsService
      .initialize(environment.applications.activities.url)
      .subscribe((result) => {
        return result;
      });
}

/**
 * Services, helpers, etc used for whole application
 */
@NgModule({
  declarations: [
    // directives
    AppDataTestDirective,
    AppModeEmbedDirective,
    AppModeFullDirective,
    LoadingDirective,
    ListItemDirective,
    ObserveElementDirective,

    // pipes
    DisplayAgeRangePipe,
    DisplayAgePipe,
    DisplayLevelPipe,
    DisplayPricePipe,
    PriceConversionPipe,
    DisplayStartingPricePipe,
    FileSizePipe,
    LocalizedDatePipe,
    Nl2BrPipe,
    SafeUrlPipe,
    SafeHtmlPipe,
    StripHtmlPipe,
  ],
  providers: [
    RedirectionService,
    CurrencyPipe,
    {
      provide: Logger,
      useExisting: ConsoleLoggerService,
    },
    {
      provide: ACTIVITIES_BASE_URL,
      useValue: environment.applications.activities.url,
    },
    {
      provide: ACTIVITIES_API_KEY,
      useValue: environment.applications.activities.api_key,
    },
    {
      provide: CDN_HOST,
      useValue: environment.applications.cdn.host,
    },
    {
      provide: CDN_ROOT,
      useValue: environment.applications.cdn.root,
    },
    {
      provide: CMS_BASE_URL,
      useValue: environment.applications.strapi.url,
    },
    {
      provide: PFS_BASE_URL,
      useValue: environment.applications.pfs.url,
    },
    {
      provide: ADVICE_BASE_URL,
      useValue: environment.applications.advice.url,
    },
    {
      provide: ADVICE_API_KEY,
      useValue: environment.applications.advice.apiKey,
    },
    {
      provide: GTM_ID,
      useValue: environment.applications.gtm.id,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useExisting: ACTIVITIES_INTERCEPTOR,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useFactory: () =>
        new APIThrottleInterceptor(
          [
            '/',
            environment.applications.front.url,
            environment.applications.activities.url,
          ],
          12
        ),
      multi: true,
    },
    {
      provide: TAILWIND_CONFIG,
      useFactory: () => {
        resolveConfig(tailwindConfig);
      },
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (matchMedia: MatchMediaService) => {
        return () => {
          matchMedia.initialize();
        };
      },
      deps: [MatchMediaService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (privacyManagerService: PrivacyManagerService) => () => {
        privacyManagerService.initialize();
        const amplitudeConsentHandlers: { [key: string]: () => void } = {
          true: () => privacyManagerService.initAmplitude(),
          false: () =>
            privacyManagerService.removeScriptSrcName(
              privacyManagerService.amplitudeScriptSrc
            ),
        };

        privacyManagerService.amplitudeConsent$.subscribe((isAllowed) => {
          const handler = amplitudeConsentHandlers[String(isAllowed)];
          if (handler) {
            handler();
          } else {
            console.warn('Unhandled consent state:', isAllowed);
          }
        });
        privacyManagerService.datadogConsent$.subscribe((isAllowed) => {
          setupDatadog(isAllowed);
        });
      },
      multi: true,
      deps: [PrivacyManagerService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: sportsServiceFactory,
      multi: true,
      deps: [SportsService],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (tagManagerService: TagManagerService) => () => {
        tagManagerService.initialize();
      },
      multi: true,
      deps: [TagManagerService],
    },
    {
      provide: APP_STORAGE,
      useFactory: (platformId: string) => {
        if (isPlatformBrowser(platformId)) {
          return localStorage;
        }
        return new DummyStorageService();
      },
      deps: [PLATFORM_ID],
    },
  ],
  imports: [CommonModule, TranslateModule],
  exports: [
    // modules
    TranslateModule,

    // directives
    AppDataTestDirective,
    AppModeEmbedDirective,
    AppModeFullDirective,
    LoadingDirective,
    ListItemDirective,
    ObserveElementDirective,

    // pipe
    DisplayAgeRangePipe,
    DisplayAgePipe,
    DisplayLevelPipe,
    DisplayPricePipe,
    PriceConversionPipe,
    DisplayStartingPricePipe,
    FileSizePipe,
    LocalizedDatePipe,
    Nl2BrPipe,
    SafeUrlPipe,
    SafeHtmlPipe,
    StripHtmlPipe,
  ],
})
export class CoreModule {}
